import React from "react";
import { Link } from "react-router-dom";

export default function ChatCardRenderer({ content }) {
  if (!content || !content.type || !content.data) return null;

  const { type, data } = content;

  if (type === "stock_summary" && data.categories) {
    const list = data.categories.slice(0, 6);
    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            📊 Live Stock Ledger ({data.totalCategories || list.length} Grades)
          </span>
          <Link
            to="/reports/stock-report"
            className="text-[11px] font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            View Full Report →
          </Link>
        </div>
        <div className="max-h-60 overflow-y-auto">
          <table className="w-full text-left text-[11px]">
            <thead className="bg-slate-50 text-slate-500 dark:bg-slate-900/50 dark:text-slate-400">
              <tr>
                <th className="px-3 py-1.5 font-medium">Category</th>
                <th className="px-2 py-1.5 font-medium text-right">Available (kg)</th>
                <th className="px-3 py-1.5 font-medium text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {list.map((cat, idx) => (
                <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                  <td className="px-3 py-1.5 font-medium text-slate-900 dark:text-slate-100 truncate max-w-[120px]">
                    {cat.categoryName}
                  </td>
                  <td className="px-2 py-1.5 text-right font-semibold text-slate-800 dark:text-slate-200">
                    {Number(cat.availableStockKg || 0).toLocaleString("en-IN", {
                      maximumFractionDigits: 1,
                    })}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    <span
                      className={`inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-medium ${
                        cat.status === "Low Stock Warning"
                          ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                          : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
                      }`}
                    >
                      {cat.status === "Low Stock Warning" ? "Low" : "Optimal"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.totalInventoryTonnageKg !== undefined && (
          <div className="border-t border-slate-100 bg-slate-50/50 px-3 py-1.5 text-[11px] font-medium text-slate-600 dark:border-slate-800 dark:bg-slate-800/30 dark:text-slate-300">
            Total Inventory:{" "}
            <span className="font-bold text-slate-900 dark:text-white">
              {Number(data.totalInventoryTonnageKg).toLocaleString("en-IN", {
                maximumFractionDigits: 1,
              })}{" "}
              kg
            </span>
          </div>
        )}
      </div>
    );
  }

  if (type === "shipments" && data.shipments) {
    const list = data.shipments.slice(0, 5);
    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            🚢 Maritime Shipments & Demurrage
          </span>
          <Link
            to="/bl-master"
            className="text-[11px] font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            BL Tracker →
          </Link>
        </div>
        <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
          {list.map((s, idx) => (
            <div key={idx} className="p-2.5 text-[11px] hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-900 dark:text-white">{s.blNumber}</span>
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                  {s.status}
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between text-slate-500 dark:text-slate-400">
                <span>Shipping: {s.shippingLine || "N/A"}</span>
                <span>Port ETA: {s.portEta || "N/A"}</span>
              </div>
              {s.demurrageRisk === "HIGH RISK - EXPIRING SOON" ? (
                <div className="mt-1.5 flex items-center gap-1 rounded bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
                  <span>⚠️ Demurrage Alert: {s.daysLeftForIcd} days left for ICD</span>
                </div>
              ) : (
                <div className="mt-1 text-[10px] text-slate-500">
                  ICD ETA: {s.icdEta || "N/A"} ({s.daysLeftForIcd} days left)
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "ap_aging" && data.payables) {
    const list = data.payables.slice(0, 5);
    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            💳 Accounts Payable (AP Aging)
          </span>
          <Link
            to="/reports/ap-aging"
            className="text-[11px] font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            Full Aging →
          </Link>
        </div>
        <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
          {list.map((p, idx) => (
            <div key={idx} className="p-2.5 text-[11px] hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
              <div className="flex items-center justify-between font-semibold text-slate-900 dark:text-white">
                <span>{p.vendorName}</span>
                <span className="text-rose-600 dark:text-rose-400">
                  ₹{Number(p.outstandingBalance || 0).toLocaleString("en-IN")}
                </span>
              </div>
              <div className="mt-0.5 flex justify-between text-[10px] text-slate-500">
                <span>Bill #{p.billNo}</span>
                <span>Due: {p.dueDate || "N/A"}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "ar_aging" && data.receivables) {
    const list = data.receivables.slice(0, 5);
    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            💰 Accounts Receivable (AR Aging)
          </span>
          <Link
            to="/reports/ar-aging"
            className="text-[11px] font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            Full Aging →
          </Link>
        </div>
        <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
          {list.map((r, idx) => (
            <div key={idx} className="p-2.5 text-[11px] hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
              <div className="flex items-center justify-between font-semibold text-slate-900 dark:text-white">
                <span>{r.customerName}</span>
                <span className="text-emerald-600 dark:text-emerald-400">
                  ₹{Number(r.outstandingBalance || 0).toLocaleString("en-IN")}
                </span>
              </div>
              <div className="mt-0.5 flex justify-between text-[10px] text-slate-500">
                <span>Invoice #{r.invoiceNo}</span>
                <span>Due: {r.dueDate || "N/A"}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (type === "directory_search" && data.results && data.results.length > 0) {
    const list = data.results.slice(0, 4);
    const getEntityConfig = (entityType) => {
      const typeKey = String(entityType || '').toLowerCase().replace(/[^a-z]/g, '');
      switch (typeKey) {
        case "customer":
          return {
            icon: "👤",
            badge: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 border-blue-200 dark:border-blue-800",
            route: "/customer-master",
            label: "Customer Profile"
          };
        case "vendor":
        case "party":
          return {
            icon: "🏢",
            badge: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300 border-purple-200 dark:border-purple-800",
            route: "/settings-master/party",
            label: "Vendor Directory"
          };
        case "yard":
          return {
            icon: "📍",
            badge: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200 dark:border-amber-800",
            route: "/yard",
            label: "Yard Master"
          };
        case "lot":
          return {
            icon: "📦",
            badge: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800",
            route: "/settings-master/lot",
            label: "Lot Inventory"
          };
        case "bl":
        case "billoflading":
          return {
            icon: "🚢",
            badge: "bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300 border-teal-200 dark:border-teal-800",
            route: "/bl-master",
            label: "BL Tracker"
          };
        case "stockcategory":
          return {
            icon: "🏷️",
            badge: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
            route: "/stock-category",
            label: "Stock Category"
          };
        case "shippingline":
          return {
            icon: "⚓",
            badge: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300 border-cyan-200 dark:border-cyan-800",
            route: "/bl-master",
            label: "Shipping Line"
          };
        default:
          return {
            icon: "📋",
            badge: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700",
            route: "/dashboard",
            label: "Directory"
          };
      }
    };

    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
              ⚡ Enterprise Match ({data.count || list.length})
            </span>
            {data.query && (
              <span className="max-w-[120px] truncate rounded bg-slate-200/70 px-1.5 py-0.5 text-[9px] font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400">
                "{data.query}"
              </span>
            )}
          </div>
          <span className="text-[10px] font-medium text-primary-600 dark:text-primary-400">
            Vector Semantic RAG
          </span>
        </div>
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {list.map((item, idx) => {
            const config = getEntityConfig(item.type);
            return (
              <div
                key={idx}
                className="p-3 text-[11px] transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${config.badge}`}
                      >
                        <span>{config.icon}</span>
                        <span>{item.type}</span>
                      </span>
                      <h4 className="truncate font-bold text-slate-900 dark:text-white">
                        {item.title}
                      </h4>
                    </div>
                    {item.subtitle && (
                      <p className="mt-1 truncate text-[11px] text-slate-600 dark:text-slate-300">
                        {item.subtitle}
                      </p>
                    )}
                    {item.metadata && Object.keys(item.metadata).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {Object.entries(item.metadata)
                          .filter(([_, v]) => v !== null && v !== undefined && v !== "")
                          .slice(0, 3)
                          .map(([k, v]) => (
                            <span
                              key={k}
                              className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                            >
                              <span className="font-semibold capitalize text-slate-500 dark:text-slate-400 mr-1">
                                {k}:
                              </span>
                              {String(v)}
                            </span>
                          ))}
                      </div>
                    )}
                  </div>
                  <Link
                    to={config.route}
                    className="shrink-0 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-primary-600 shadow-2xs hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-primary-400 dark:hover:bg-slate-700"
                  >
                    Open →
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (type === "enterprise_query" && data.rows && data.rows.length > 0) {
    const rows = data.rows.slice(0, 5);
    const columns = Object.keys(rows[0] || {}).slice(0, 4);

    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-800/50">
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">
            📊 Database Insights ({data.rowCount || rows.length} Records)
          </span>
          <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
            Safe SQL
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[11px]">
            <thead className="bg-slate-50 text-slate-500 dark:bg-slate-900/50 dark:text-slate-400">
              <tr>
                {columns.map((col) => (
                  <th key={col} className="px-3 py-1.5 font-medium capitalize">
                    {col.replace(/([A-Z])/g, " $1")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((r, idx) => (
                <tr key={idx} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                  {columns.map((col) => (
                    <td
                      key={col}
                      className="px-3 py-1.5 font-medium text-slate-900 dark:text-slate-100 truncate max-w-[140px]"
                    >
                      {r[col] !== null && r[col] !== undefined ? String(r[col]) : "-"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return null;
}

